"use client";

import clsx from "clsx";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import StripePricingTable from "@/components/common/StripePricingTable";
import FancyLoader from "@/components/common/fancyLoader";
import Banner from "@/components/library/Banner";
import Modal from "@/components/library/Modal";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { BANNER_CONDITIONS } from "@/enums/banner";
import { SubscriptionStatus } from "@/enums/stripe";
import { apiLive } from "@/lib/api";
import { AgencyStatusResponse } from "@/types/agencies";
import { BannerItem, SITES } from "@/types/common/banner";
import { OrgFeatures } from "@/types/org";
import {
  buildBannerAcknowledgement,
  findUserAcknowledgement,
  getUserAcknowledgements,
  setUserAcknowledgement,
} from "@/utils/acknowledgements/userAcknowledgements";
import { HandleOpenBillingSession } from "@/utils/agencies/settings";
import {
  acknowledgeBanner,
  getAcknowledgedBannerIds,
  sortBannersByPriority,
} from "@/utils/banners";
import { shouldShowTermsAcceptanceModal } from "@/utils/common/admin";
import { BASE_MARKETING_URL } from "@/utils/common/baseUrls";
import { collectLangString } from "@/utils/common/langString";
import { usePostHog } from "@posthog/next";
import { useAlert } from "./useAlert";
import { useOrg } from "./useOrg";
import { useTrialUsage } from "./useTrialUsage";

type I18nResourceAccessor = (lng: string, ns: string, key: string) => unknown;

type BannerProps = {
  bannerItems: BannerItem[];
  setBannerItems: (items: BannerItem[]) => void;
  isBannerActive: boolean;
};

let hasShownSubscriptionModal = false;

export function filterBannersLogic(
  bannerItems: BannerItem[],
  acknowledgedIds: string[],
  orgFeatures: OrgFeatures | null | undefined
): BannerItem[] {
  let filteredBanners = bannerItems.filter((banner) => !acknowledgedIds.includes(banner.id));

  const hasConditions = filteredBanners.some((banner) => banner.condition);

  if (hasConditions) {
    let showMigration = false;

    if (orgFeatures && !orgFeatures.campaignVersion.includes("2")) {
      showMigration = true;
    }

    filteredBanners = filteredBanners.filter((banner) => {
      if (!banner.condition) {
        return true;
      }

      if (banner.condition === BANNER_CONDITIONS.MIGRATION) {
        return showMigration;
      }

      return false;
    });
  }

  return filteredBanners;
}

export function getBannerItemFromSubscription(
  status: AgencyStatusResponse,
  getResource: I18nResourceAccessor,
  onTrialing?: () => void
): BannerItem | null {
  if (!status || status.status === "active") {
    return null;
  }

  if (status.status === SubscriptionStatus.none) {
    return null;
  } else if (status.status === SubscriptionStatus.trialing) {
    onTrialing?.();
    return null;
  }

  return {
    id: "canceled",
    status: "error",
    description: collectLangString(
      getResource,
      "trialUsage",
      "banners.subscriptionEnded.description"
    ),
    icon: "circle-exclamation",
    isPersistent: true,
    link: "/settings",
    linkText: collectLangString(getResource, "trialUsage", "banners.subscriptionEnded.linkText"),
    sites: [SITES.DASHBOARD],
    createdAt: "",
    updatedAt: "",
  };
}

export function getProductsCanceledBanner(
  allCanceled: boolean,
  getResource: I18nResourceAccessor
): BannerItem | null {
  if (!allCanceled) return null;
  return {
    id: "products-canceled",
    status: "error",
    description: collectLangString(
      getResource,
      "trialUsage",
      "banners.productsCanceled.description"
    ),
    icon: "circle-exclamation",
    isPersistent: true,
    link: "/settings",
    linkText: collectLangString(getResource, "trialUsage", "banners.productsCanceled.linkText"),
    sites: [SITES.DASHBOARD],
    createdAt: "",
    updatedAt: "",
  };
}

/**
 * Returns a blocking error banner when the organization has exhausted its
 * trial call/conversation quota (surfaced via Galactus middleware). Returns
 * null when the widget is hidden or the limit is not yet reached.
 */
export function getTrialLimitReachedBanner(
  isReached: boolean,
  getResource: I18nResourceAccessor
): BannerItem | null {
  if (!isReached) return null;
  return {
    id: "trial-limit-reached",
    status: "error",
    description: collectLangString(
      getResource,
      "trialUsage",
      "banners.trialLimitReached.description"
    ),
    icon: "circle-exclamation",
    isPersistent: true,
    link: "/get-gail",
    linkText: collectLangString(getResource, "trialUsage", "banners.trialLimitReached.linkText"),
    sites: [SITES.DASHBOARD],
    createdAt: "",
    updatedAt: "",
  };
}

export function getProvisioningPendingBanner(
  isPending: boolean,
  getResource: I18nResourceAccessor
): BannerItem | null {
  if (!isPending) return null;
  return {
    id: "provisioning-pending",
    status: "warning",
    description: collectLangString(
      getResource,
      "trialUsage",
      "banners.provisioningPending.description"
    ),
    icon: "hourglass-half",
    isPersistent: true,
    link: "/",
    linkText: collectLangString(getResource, "trialUsage", "banners.provisioningPending.linkText"),
    sites: [SITES.DASHBOARD],
    createdAt: "",
    updatedAt: "",
  };
}

export const BannerContext = createContext<BannerProps>({
  bannerItems: [],
  setBannerItems: () => {},
  isBannerActive: false,
});

export const useBanner = () => useContext(BannerContext);

const useBannerFunction = (): BannerProps => {
  const { org } = useOrg();
  const { user } = useAuthContext();

  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [allBanners, setAllBanners] = useState<BannerItem[]>([]);
  const [acknowledgedBannerIds, setAcknowledgedBannerIds] = useState<string[]>([]);
  const allBannersRef = useRef<BannerItem[]>(allBanners);

  const resolveAcknowledgedBannerIds = useCallback(
    async (bannerItems: BannerItem[]): Promise<string[]> => {
      const localAcknowledgedIds = getAcknowledgedBannerIds();
      if (!user?.id) return localAcknowledgedIds;

      try {
        const acknowledgements = await getUserAcknowledgements(user.id);
        const serverAcknowledgedIds = bannerItems
          .filter((banner) =>
            findUserAcknowledgement(acknowledgements, buildBannerAcknowledgement(banner.id))
          )
          .map((banner) => banner.id);
        const bannerIds = new Set(bannerItems.map((banner) => banner.id));

        localAcknowledgedIds
          .filter((id) => bannerIds.has(id) && !serverAcknowledgedIds.includes(id))
          .forEach((id) => {
            void setUserAcknowledgement(user.id, buildBannerAcknowledgement(id), "dismissed", {
              source: "legacy_banner_local_storage",
            });
          });

        return Array.from(new Set([...localAcknowledgedIds, ...serverAcknowledgedIds]));
      } catch {
        return localAcknowledgedIds;
      }
    },
    [user?.id]
  );

  const filterBanners = (bannerItems: BannerItem[], acknowledgedIds = acknowledgedBannerIds) => {
    const alreadyAckedIds = Array.from(
      new Set([...getAcknowledgedBannerIds(), ...acknowledgedIds])
    );
    const filtered = filterBannersLogic(bannerItems, alreadyAckedIds, org?.features);
    setBanners(sortBannersByPriority(filtered));
  };

  const fetchBanners = async () => {
    const bannerItems = await apiLive<BannerItem[]>("/api/banner");

    if (!bannerItems) {
      setBanners([]);
      setAllBanners([]);
      return;
    }

    setAllBanners(bannerItems);
    allBannersRef.current = bannerItems;
    const resolvedAcknowledgedIds = await resolveAcknowledgedBannerIds(bannerItems);
    setAcknowledgedBannerIds(resolvedAcknowledgedIds);
    filterBanners(bannerItems, resolvedAcknowledgedIds);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    filterBanners(allBanners);
  }, [org, acknowledgedBannerIds]);

  useEffect(() => {
    // Listen for storage events (changes from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "GAIL_banner_ids") {
        const acknowledgedIds = Array.from(
          new Set([...acknowledgedBannerIds, ...getAcknowledgedBannerIds()])
        );
        setAcknowledgedBannerIds(acknowledgedIds);
        filterBanners(allBannersRef.current, acknowledgedIds);
      }
    };

    // Listen for custom events (changes from same tab)
    const handleCustomStorageChange = () => {
      const acknowledgedIds = Array.from(
        new Set([...acknowledgedBannerIds, ...getAcknowledgedBannerIds()])
      );
      setAcknowledgedBannerIds(acknowledgedIds);
      filterBanners(allBannersRef.current, acknowledgedIds);
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, [acknowledgedBannerIds]);

  return {
    bannerItems: banners,
    setBannerItems: setBanners,
    isBannerActive: banners.length > 0,
  };
};

export const BannerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const banner = useBannerFunction();
  const { subscription, user, provisioningPending } = useAuthContext();
  const { setAlert, setIsLoading } = useAlert();
  const { saveOrg, productAccess } = useOrg();
  const { shouldShow: trialUsageActive, isLimitReached: trialLimitReached } = useTrialUsage();
  const { t: ot, i18n } = useTranslation("onboarding");
  const posthog = usePostHog();

  const { bannerItems, setBannerItems } = banner;

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState({
    show: false,
    clientSecret: "",
  });
  const [items, setItems] = useState<BannerItem[]>([]);

  // Create the banner value with actual visible items
  const bannerValue: BannerProps = useMemo(
    () => ({
      bannerItems,
      setBannerItems,
      isBannerActive: items.length > 0,
    }),
    [bannerItems, setBannerItems, items.length]
  );

  const openSubscriptionModal = async (source: "timer" | "click") => {
    if (hasShownSubscriptionModal && source === "timer") {
      return;
    }

    if (source === "timer") {
      hasShownSubscriptionModal = true;
    }

    const isTimerSource = source === "timer";
    const loadingMessage = isTimerSource ? ot("preparingBilling") : ot("startingSubscription");
    const loadingDescription = isTimerSource
      ? ot("pleaseWaitPrepareBilling")
      : ot("pleaseWaitStartSubscription");

    setIsLoading(true, loadingMessage, loadingDescription);

    //Call the API to start the stripe creation process
    const response = await apiLive<{
      clientSecret: string;
      success: boolean;
    }>("/api/billing/create");

    //Handle response based on source
    if (response?.success) {
      // Only show modal for click-based calls, not timer-based
      if (!isTimerSource) {
        setShowSubscriptionModal({
          show: true,
          clientSecret: response.clientSecret,
        });
        posthog?.capture(`SubscriptionModalOpen`);
      }
    } else {
      const errorMessage = isTimerSource
        ? ot("failedToPrepareBilling")
        : ot("failedToStartSubscription");
      setAlert({
        status: "error",
        message: errorMessage,
        secondsToClose: 10,
      });
    }

    setIsLoading(false, "");
  };

  useEffect(() => {
    if (subscription.status === SubscriptionStatus.none) {
      // Create billing in background after 2 minutes but don't show modal
      const timer = setTimeout(() => {
        openSubscriptionModal("timer");
      }, 120000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [subscription]);

  const shouldShowTermsModal = shouldShowTermsAcceptanceModal(
    subscription.flags.needs_signing,
    user
  );

  useEffect(() => {
    setShowTermsModal(shouldShowTermsModal);
  }, [shouldShowTermsModal]);

  useEffect(() => {
    const getResource: I18nResourceAccessor = (lng, ns, key) => i18n.getResource(lng, ns, key);

    const subscriptionBannerItem = getBannerItemFromSubscription(subscription, getResource, () =>
      posthog?.capture(`Trialing`)
    );

    if (subscriptionBannerItem?.status === "error") {
      setShowBillingModal(true);
    }

    const trialLimitBanner = getTrialLimitReachedBanner(
      trialUsageActive && trialLimitReached,
      getResource
    );

    const productsCanceledBanner = getProductsCanceledBanner(
      productAccess.allProductsCanceled,
      getResource
    );

    const provisioningBanner = getProvisioningPendingBanner(provisioningPending, getResource);

    // Preference order for the single visible subscription/usage banner:
    // 1. trial usage limit reached (most actionable — hard block)
    // 2. all products canceled
    // 3. subscription status banner (trial ending, canceled, etc.)
    // Prepended because the renderer shows items[0], so it must outrank
    // any API-fetched banners already in bannerItems. Provisioning sits
    // after those so it does not hide billing-critical banners.
    const priorityBanner =
      trialLimitBanner ??
      productsCanceledBanner ??
      subscriptionBannerItem ??
      provisioningBanner ??
      null;

    setItems([...(priorityBanner ? [priorityBanner] : []), ...bannerItems]);
  }, [
    subscription,
    bannerItems,
    productAccess.allProductsCanceled,
    trialUsageActive,
    trialLimitReached,
    provisioningPending,
    i18n,
    posthog,
  ]);

  return (
    <BannerContext.Provider value={bannerValue}>
      {items.length > 0 ? (
        <Banner
          key={items[0].id}
          item={items[0]}
          handleRemoveBanner={async (id: string) => {
            acknowledgeBanner(id);
            if (user?.id) {
              void setUserAcknowledgement(user.id, buildBannerAcknowledgement(id), "dismissed", {
                source: "banner",
              });
            }
            setItems(items.slice(1));
          }}
        />
      ) : null}
      {children}
      <Modal
        open={showTermsModal}
        onOpenChange={() => {}}
        header={{
          title: ot("termsModal.title"),
          className: "[&_button]:hidden",
        }}
        buttons={[
          {
            type: "primary",
            onClick: async () => {
              setShowTermsModal(false);
              if (!shouldShowTermsModal) {
                return;
              }

              await saveOrg({
                details: {
                  needs_signing: false,
                },
              });
            },
            children: ot("termsModal.iAgree"),
          },
        ]}
      >
        <p>{ot("termsModal.excitedMessage")}</p>
        <p className={clsx("mt-4")}>
          <Trans
            i18nKey="termsModal.reviewTerms"
            ns="onboarding"
            components={{
              termsLink: (
                <a
                  href={`${BASE_MARKETING_URL}/terms-and-conditions`}
                  target="_blank"
                  className={clsx("text-primary")}
                  rel="noopener noreferrer"
                  aria-label={ot("termsAndConditions")}
                />
              ),
            }}
          />
        </p>
        <p className={clsx("mt-4")}>
          <Trans
            i18nKey="termsModal.reviewPrivacy"
            ns="onboarding"
            components={{
              privacyLink: (
                <a
                  href={`${BASE_MARKETING_URL}/privacy-policy`}
                  target="_blank"
                  className={clsx("text-primary")}
                  rel="noopener noreferrer"
                  aria-label={ot("privacyPolicy")}
                />
              ),
            }}
          />
        </p>
      </Modal>
      <Modal
        open={showBillingModal}
        onOpenChange={(open) => !open && setShowBillingModal(false)}
        header={{
          title: ot("billingModal.title"),
        }}
        buttons={[
          {
            type: "primary",
            onClick: () => {
              setShowBillingModal(false);
              HandleOpenBillingSession(setAlert);
            },
            iconBefore: "address-card",
            children: ot("billingModal.updateBilling"),
          },
        ]}
      >
        <p>{ot("billingModal.description")}</p>
      </Modal>
      <Modal
        open={showSubscriptionModal.show}
        onOpenChange={(open) => {
          if (!open) {
            setShowSubscriptionModal({
              show: false,
              clientSecret: showSubscriptionModal.clientSecret,
            });
          }
        }}
        size="xl"
        header={{
          title: ot("subscriptionModal.title"),
        }}
        buttons={[
          {
            type: "secondary",
            onClick: () =>
              setShowSubscriptionModal({
                show: false,
                clientSecret: showSubscriptionModal.clientSecret,
              }),
            children: ot("subscriptionModal.continueTrying"),
          },
        ]}
      >
        <div className={clsx("mb-6", "grid", "gap-4", "grid-cols-1", "md:grid-cols-2")}>
          <div className={clsx("max-w-sm", "mx-auto")}>
            <span className={clsx("mb-2", "text-lg", "block")}>
              {ot("subscriptionModal.youCan")}
            </span>
            <ul className={clsx("list-disc", "mx-auto", "inline-block", "mb-4", "list-inside")}>
              <li>{ot("subscriptionModal.continueTryingGail")}</li>
              <li>{ot("subscriptionModal.bookDemo")}</li>
              <li className={clsx("font-semibold")}>
                {ot("subscriptionModal.purchaseAndSchedule")}
              </li>
            </ul>
          </div>
          <div className={clsx("max-w-sm", "mx-auto")}>
            {ot("subscriptionModal.excitedMessage")}
          </div>
        </div>
        <div className={clsx("grid", "checkout_container")}>
          {showSubscriptionModal.clientSecret ? (
            <StripePricingTable
              pricing-table-id={process.env.NEXT_PUBLIC_STRIPE_PRICING_TABLE_ID || ""}
              publishable-key={process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ""}
              customer-session-client-secret={showSubscriptionModal.clientSecret}
            />
          ) : (
            <FancyLoader />
          )}
        </div>
      </Modal>
    </BannerContext.Provider>
  );
};
